import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Info, Loader2, RefreshCw } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import TrodoTooltip from 'shared/components/ui/TrodoTooltip';
import { getSlackChannels } from 'features/agents/api/agentsApi';

const labelCls = 'block text-[12px] text-[color:var(--text-muted)]';

const TOOLTIP = (
  <div className="space-y-1.5">
    <p>Only Slack channels and DMs that the Trodo bot can use are shown here.</p>
    <p>Connecting Slack does not automatically give the bot access to every channel.</p>
    <p>Private channels will appear after the Trodo bot is invited to them.</p>
    <p>If you don&apos;t see a channel, invite the Trodo bot to that channel in Slack and then refresh this list.</p>
  </div>
);

function isExpression(value) {
  return typeof value === 'string' && value.includes('{{');
}

export function operationForNodeKind(kind) {
  if (!kind || typeof kind !== 'string') return 'sendMessage';
  if (kind.startsWith('slack_trigger_')) return 'listenEvent';
  const rest = kind.replace(/^slack_/, '');
  if (!rest || rest === kind) return 'sendMessage';
  return rest.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}

function conversationKindLabel(ch) {
  if (ch.is_im) return 'Direct message';
  if (ch.is_mpim) return 'Group DM';
  if (ch.type === 'private' || ch.is_private) return 'Private';
  return 'Public';
}

function conversationTitle(ch) {
  if (ch.is_im) {
    const n = String(ch.name || '').trim();
    if (n && n !== 'Direct message' && !/^DM U/i.test(n) && !/^U[A-Z0-9]{8,}$/i.test(n)) return n;
    return 'Direct message';
  }
  if (ch.is_mpim) return ch.name ? String(ch.name).replace(/^#/, '') : 'Group DM';
  const n = String(ch.name || '').replace(/^#/, '');
  return n ? `#${n}` : ch.id;
}

/**
 * Team-bot conversation picker. Stores Slack conversation ID.
 * Lists only conversations the backend marks usable for the current operation.
 */
export default function SlackChannelPicker({
  field,
  value,
  onChange,
  nodeKind,
  /** The private-channel note under the control. Off where the page is already terse. */
  showHelp = true,
}) {
  const operation = field?.operation || operationForNodeKind(nodeKind);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setLoadError(false);
    try {
      const rows = await getSlackChannels({ refresh, operation });
      setChannels(Array.isArray(rows) ? rows : []);
    } catch (_) {
      setChannels([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [operation]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    if (loading || loadError || isExpression(value) || !value) return;
    if (!channels.some((c) => c.id === value)) onChange('');
  }, [channels, loading, loadError, value, onChange]);

  const options = useMemo(
    () => channels.map((ch) => ({
      value: ch.id,
      label: conversationTitle(ch),
      hint: conversationKindLabel(ch),
    })),
    [channels],
  );

  const refreshLabel = loading ? 'Refreshing...' : 'Refresh';

  const helpFooter = (
    <p className="text-[11px] leading-snug text-[color:var(--text-muted)] mt-2">
      Don&apos;t see your channel? Private channels only appear after the Trodo bot
      has been given access to them.
    </p>
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-1 min-w-0">
          <label className={labelCls}>{field.label || 'Channel or DM'}</label>
          <TrodoTooltip content={TOOLTIP} placement="bottom">
            <button
              type="button"
              aria-label="About Channel or DM"
              className="inline-flex items-center justify-center rounded-sm text-[color:var(--text-disabled)] hover:text-[color:var(--text-muted)] focus:outline-none focus-visible:ring-1 focus-visible:ring-[color:var(--border-strong)]"
            >
              <Info className="w-3 h-3" strokeWidth={2} />
            </button>
          </TrodoTooltip>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          className="inline-flex items-center gap-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          {refreshLabel}
        </button>
      </div>

      {loadError ? (
        <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5">
          <div className="text-[13px] font-medium text-[color:var(--text-primary)]">Couldn&apos;t load Slack channels</div>
          <p className="text-[11px] text-[color:var(--text-muted)] mt-0.5">Check your Slack connection and try again.</p>
          <button
            type="button"
            onClick={() => load(true)}
            className="mt-2 text-[12px] font-medium text-[color:var(--text-primary)] underline"
          >
            Retry
          </button>
        </div>
      ) : !loading && channels.length === 0 ? (
        <div className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-3 py-2.5">
          <div className="text-[13px] font-medium text-[color:var(--text-primary)]">No Slack channels available</div>
          <p className="text-[11px] text-[color:var(--text-muted)] mt-0.5 leading-snug">
            There are currently no Slack channels or DMs that the Trodo bot can use for this action.
            If you need to use a private channel, invite the Trodo bot to it in Slack and refresh.
          </p>
          <button
            type="button"
            onClick={() => load(true)}
            className="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-[color:var(--text-primary)] underline"
          >
            Refresh channels
          </button>
        </div>
      ) : (
        <>
          <BrandDropdown
          triggerClassName="!h-8 !px-2 !py-0 !bg-transparent"
            value={value ?? ''}
            onChange={onChange}
            options={options}
            placeholder={loading ? 'Loading…' : 'Search channels or DMs...'}
            searchable
            searchPlaceholder="Search channels or DMs..."
            emptyMessage="No available channels or DMs match your search."
            loading={loading}
            ariaLabel={field.label || 'Channel or DM'}
            renderTrigger={(sel) => (sel ? (
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="truncate">{sel.label}</span>
                {sel.hint ? <span className="shrink-0 text-[11px] text-[color:var(--text-muted)]">{sel.hint}</span> : null}
              </span>
            ) : (loading ? 'Loading…' : 'Select a channel or DM'))}
          />
          {showHelp && helpFooter}
        </>
      )}
    </div>
  );
}
